//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@tmt-insights/oms_fe_core';
import { getAgeFromCreatedAndCompleted } from '@tmt-insights/oms_fe_core/date';
import { Tooltip, Typography } from '@tmt-insights/oms_fe_core/material';
import { getLineageMetaTitle } from '@tmt-insights/oms_fe_core/utils';
import {
  AssigneeSelect,
  DateTimeDisplay,
  History,
  OverflowMenu,
  PolarisIcon,
  PriorityNumber,
  TableStatus,
  Notes,
  TaskType
} from '@tmt-insights/oms_fe_core/columns';
import { OrderJobTask, UserProfile } from '@/redux/slices/slice_types';
import TaskActionButton from '../../components/TaskActionButton';

const columns: AllTableColumnsConfig<OrderJobTask> = {
  priority: {
    label: (
      <Tooltip title="Priority">
        <PolarisIcon icon="priority" height={20} style={{ verticalAlign: 'middle' }} />
      </Tooltip>
    ),
    minWidth: 50,
    render: (cell: OrderJobTask['priority']) => {
      return (
        <Tooltip title="Filter by Assignee to assign Priority">
          <div style={{ position: 'relative' }}>
            {/* negative margin and padding to force tooltip hover area to be larger */}
            {Number(cell) ? (
              <PriorityNumber priority={cell} />
            ) : (
              <div style={{ display: 'block', margin: -20, padding: 20 }}>
                <PolarisIcon icon="empty" height={10} style={{ marginLeft: 0 }} />
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
    draggable: (_cell: any, row: OrderJobTask) => row.status !== 'complete' && row.status !== 'cancelled' && !!row.assigned_user
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobTask['hid']) => <strong>{cell}</strong>
  },
  step: {
    label: 'Task Type',
    sortField: 'step',
    minWidth: 150,
    render: (cell: OrderJobTask['step'], row: OrderJobTask) => <TaskType type={cell} prettyName={row.pretty_name} />
  },
  job__due_date: {
    label: 'Due Date',
    minWidth: 100,
    render: (cell: OrderJobTask['job']['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at_dateformat: {
    sortField: 'created_at',
    label: 'Created',
    minWidth: 120,
    render: (_cell: OrderJobTask['created_at'], row: OrderJobTask) => <DateTimeDisplay>{row.created_at}</DateTimeDisplay>
  },
  updated_at: {
    sortField: 'updated_at',
    label: 'Last Updated',
    minWidth: 120,
    render: (_cell: OrderJobTask['updated_at'], row: OrderJobTask) => <DateTimeDisplay>{row.updated_at}</DateTimeDisplay>
  },
  created_at: {
    label: 'Age',
    minWidth: 120,
    disableSort: true,
    render: (cell: OrderJobTask['created_at'], row: OrderJobTask) => (
      <div style={{ whiteSpace: 'nowrap' }}>{getAgeFromCreatedAndCompleted(row.created_at, row.completed_at)}</div>
    )
  },
  job__title__name: {
    label: 'Title',
    render: (cell: OrderJobTask['job']['title']['name'], row: OrderJobTask) =>
      !cell ? (
        row.job?.title?.name
      ) : (
        <>
          <Typography>
            <strong>{cell}</strong>
          </Typography>
          <Typography variant="caption">{getLineageMetaTitle(row.job.title)}</Typography>
        </>
      )
  },
  status: {
    label: 'Status',
    minWidth: 120,
    render: (cell: OrderJobTask['status'], row: OrderJobTask) => (
      // only display status reason if status is failed or in_error
      <TableStatus status={cell} statusReason={row.status === 'failed' || row.status === 'in_error' ? row.status_reason : undefined} />
    )
  },
  assigned_user: {
    label: 'Assignee',
    minWidth: 135,
    render: (cell: OrderJobTask['assigned_user'], row: OrderJobTask) => (
      <AssigneeSelect value={cell as UserProfile} id={row.id} assigns="user" priority={row.priority} />
    ),
    disableSort: true
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobTask) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} />
      </div>
    ),
    disableSort: true
  },
  action: {
    label: 'Action',
    disableSort: true,
    minWidth: 75,
    render: (_cell: any, row: OrderJobTask) => <TaskActionButton row={row} showPlayButton />
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: OrderJobTask) => (
      <OverflowMenu>
        <History data={row} />
      </OverflowMenu>
    )
  }
};

export default columns;
